import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { MemberFormRule, MemberFormSchema } from "@factory/form/form.rule";
import { useFormDefaultMember } from "@factory/form/form-default-member.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateMember,
  useQyGetMemberById,
  useQyDeleteMember,
  useQyGetAllMemberById,
} from "@factory/query/member.query";
import { GetResponseMemberModel } from "@factory/model/query-model-member";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { useQyGetGoogleCloudAssetPreview } from "@factory/query/google-cloud-storage.query";

export function useEditMember() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultMember();
  const navigate = useNavigate();
  const { mapUpdateMember, mapDeleteMember } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { memberId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Member
  const handleGetApiSuccess = (data: GetResponseMemberModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    if (responseData?.avatar) {
      viewAsset(responseData?.avatar, {
        onSuccess(data) {
          const assetDisplay = URL.createObjectURL(data);
          setValue("photo", assetDisplay);
        },
      });
    }

    setValue("tenant", responseData?.tenant || "");
    setValue("parentCode", responseData?.parent_code || "");
    setValue("firstName", responseData?.first_name || "");
    setValue("middleName", responseData?.middle_name || "");
    setValue("lastName", responseData?.last_name || "");
    setValue("email", responseData?.email || "");
    setValue("gender", responseData?.gender || "");
    setValue(
      "birthday",
      responseData?.birthday ? formatDate(responseData?.birthday) : ""
    );
    setValue("mobileNumber", responseData?.mobile_number || "");
    setValue("avatar", responseData?.avatar || "");
    setValue(
      "emergencyContact_1Number",
      responseData?.emergency_contact_1_number || ""
    );
    setValue(
      "emergencyContact_1Name",
      responseData?.emergency_contact_1_name || ""
    );
    setValue(
      "emergencyContact_1Relationship",
      responseData?.emergency_contact_1_relationship || ""
    );
    setValue(
      "emergencyContact_2Number",
      responseData?.emergency_contact_2_number || ""
    );
    setValue(
      "emergencyContact_2Name",
      responseData?.emergency_contact_2_name || ""
    );
    setValue(
      "emergencyContact_2Relationship",
      responseData?.emergency_contact_2_relationship || ""
    );
    setValue("streetName", responseData?.street_name || "");
    setValue("subdivision", responseData?.subdivision || "");
    setValue("zone", responseData?.zone || "");
    setValue("sitio", responseData?.sitio || "");
    setValue("purok", responseData?.purok || "");
    setValue("barangay", responseData?.barangay || "");
    setValue("municipality", responseData?.municipality || "");
    setValue("province", responseData?.province || "");
    setValue("region", responseData?.region || "");
    setValue("country", responseData?.country || "");
    setValue("zip", responseData?.zip || "");
    setValue("longitude", responseData?.longitude);
    setValue("latitude", responseData?.latitude);
    setValue("isChild", Boolean(responseData?.is_child));
  };
  const {
    data: memberResponse,
    isLoading,
    isError: memberError,
  } = useQyGetAllMemberById(memberId || "", handleGetApiSuccess);
  const memberData = memberResponse?.data?.[0];

  // API VIEW ASSET
  const { mutate: viewAsset } = useQyGetGoogleCloudAssetPreview();

  // API UPDATE Member
  const handleUpdateApiSuccess = () => {
    showSuccess("Member updated successfully");
  };
  const { mutate: updateMember, isLoading: isEditLoading } = useQyUpdateMember(
    handleUpdateApiSuccess
  );

  // API DELETE Member
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Member deleted successfully");
  };
  const { mutate: deleteMember } = useQyDeleteMember(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<MemberFormSchema>({
    defaultValues: getDefault(memberData),
    resolver: zodResolver(MemberFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: MemberFormSchema) => {
    if (!memberData || !memberData.code) {
      throw new Error("No member data");
    }

    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapUpdateMember(updatedForm, memberData.code);
    updateMember(formData);
  };
  const handleValidateError = (err: FieldErrors<MemberFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!memberData || !memberData.code) {
      throw new Error("No member data found");
    }

    const form = mapDeleteMember(memberData.code);
    deleteMember(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    memberId,
    memberData,
    isLoading,
    memberError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
